import { CSSProperties, useCallback, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Collapsible } from '@cbhq/cds-web/collapsible';
import { Divider, HStack, VStack } from '@cbhq/cds-web/layout';
import { AnimatedCaret } from '@cbhq/cds-web/motion/AnimatedCaret';
import { Interactable } from '@cbhq/cds-web/system';
import { TextLabel1 } from '@cbhq/cds-web/typography';

import { useProfileMenuV2 } from '../../providers/ProfileMenuV2Provider';
import { ProfilesType } from '../../types';

import { AddAccountButton } from './AddAccountButton';
import { AvatarStack } from './AvatarStack';
import { Profile } from './Profile';

type Props = {
  otherProfiles: ProfilesType;
};

const messages = defineMessages({
  otherAccounts: {
    defaultMessage: 'Other accounts',
    id: 'Other accounts',
    description: 'Label for other accounts in the user switcher menu.',
  },
});

const otherAccountHeaderStyle: CSSProperties = {
  cursor: 'pointer',
};

export function OtherAccounts({ otherProfiles }: Props) {
  const { labels } = useProfileMenuV2();
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const { formatMessage } = useIntl();

  const handleToggle = useCallback(() => {
    setCollapsed((prev) => !prev);
  }, []);

  const otherAcctLabel = useMemo(
    () =>
      labels.find((label) => label.key === 'OTHER_ACCOUNTS')?.value ??
      formatMessage(messages.otherAccounts),
    [formatMessage, labels],
  );

  if (!otherProfiles || otherProfiles.length === 0) {
    return <AddAccountButton />;
  }

  return (
    <VStack width="100%">
      <Interactable as="button" background="transparent" onClick={handleToggle} width="100%">
        <HStack
          width="100%"
          height="40px"
          justifyContent="space-between"
          paddingY={1}
          paddingX={2}
          style={otherAccountHeaderStyle}
          alignItems="center"
        >
          <TextLabel1 as="p" color="fgMuted" display="block">
            {otherProfiles.length === 1
              ? otherAcctLabel
              : `${otherAcctLabel} (${otherProfiles.length})`}
          </TextLabel1>
          <HStack alignItems="center" gap={1}>
            {collapsed && <AvatarStack />}
            <AnimatedCaret rotate={collapsed ? 90 : 0} color="fg" size="s" />
          </HStack>
        </HStack>
      </Interactable>
      <Collapsible collapsed={collapsed}>
        <VStack width="100%">
          {otherProfiles.map((profile, index) => (
            <Profile key={profile.userUuid} index={index} disabled={collapsed} {...profile} />
          ))}
          {otherProfiles.length < 4 && <AddAccountButton />}
        </VStack>
      </Collapsible>
      <Divider />
    </VStack>
  );
}
